import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import AgencyDashboard from "@/components/agency/AgencyDashboard";
import AgencySubaccounts from "@/components/agency/AgencySubaccounts";
import AgencyWhitelabel from "@/components/agency/AgencyWhitelabel";
import { AgencyUsers } from "@/components/agency/AgencyUsers";

type TabId = "dashboard" | "subcontas" | "whitelabel" | "usuarios";

interface Tab {
  id: TabId;
  label: string;
  component: React.ReactNode;
}

const Agency: React.FC = () => {
  const { t } = useTranslation(["agency", "common"]);
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState<TabId>("dashboard");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]" />
          <p className="text-sm text-muted-foreground">{t("common:shared.loading")}</p>
        </div>
      </div>
    );
  }

  if (!user || user.type !== "agency_user") {
    console.warn("⚠️ [Agency] Acesso negado: usuário não é agency_user");
    return <Navigate to="/" replace />;
  }

  const tabs: Tab[] = [
    {
      id: "dashboard",
      label: t("shell.tabDashboard"),
      component: <AgencyDashboard />,
    },
    {
      id: "subcontas",
      label: t("shell.tabSubaccounts"),
      component: <AgencySubaccounts />,
    },
    {
      id: "usuarios",
      label: t("shell.tabUsers"),
      component: <AgencyUsers />,
    },
    {
      id: "whitelabel",
      label: t("shell.tabWhitelabel"),
      component: <AgencyWhitelabel />,
    },
  ];

  return (
    <div className="w-full flex h-[calc(100dvh-120px)] flex-col bg-background text-foreground">
      <h2 className="text-2xl xl:text-3xl mb-4 font-bold tracking-tight text-foreground">
        {t("shell.title")}
      </h2>

      <div className="flex gap-1 border-b border-border mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              "px-5 py-3 text-sm font-medium transition-colors duration-200 relative rounded-t-xl",
              activeTab === tab.id
                ? "text-[#2563EB] border-b-2 border-[#2563EB] dark:text-[#60A5FA] dark:border-[#60A5FA]"
                : "text-muted-foreground hover:text-[#2563EB] hover:bg-[#F1F5F9] dark:hover:text-[#60A5FA] dark:hover:bg-[#27272A]"
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-auto">{tabs.find((tab) => tab.id === activeTab)?.component}</div>
    </div>
  );
};

export default Agency;
